import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from 'Utils/api';
import styled from 'styled-components';

const Wrap = styled.form`
  margin-top: 2.5rem;
  padding: 2rem;
  background: #fafaf6;
  border: 1px solid #e7e7e0;
  border-radius: 6px;
  font-family: 'Abel', sans-serif;
`;
const Row = styled.div<{ cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ cols }) => cols || 1}, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;
const Field = styled.div` display: flex; flex-direction: column; gap: 0.35rem; `;
const Label = styled.label`
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  color: #4d4d4d;
`;
const baseField = `
  font-family: 'Abel', sans-serif;
  font-size: 0.95rem;
  padding: 0.7rem 0.9rem;
  border: 1px solid #cdcdc4;
  border-radius: 4px;
  background: #fff;
  &:focus { outline: none; border-color: #657455; }
`;
const Input = styled.input` ${baseField} `;
const TextArea = styled.textarea` ${baseField} resize: vertical; min-height: 200px; `;
const CheckRow = styled.label`
  display: flex; gap: 0.6rem; align-items: flex-start;
  font-size: 0.85rem; line-height: 1.55; margin-bottom: 1rem; color: #555;
  cursor: pointer;
  input { margin-top: 4px; accent-color: #657455; }
  a { color: #657455; text-decoration: underline; }
`;
const Submit = styled.button`
  background: #b8964a; color: #fff; border: none; padding: 0.95rem 2.2rem;
  font-family: 'Abel', sans-serif; font-size: 0.95rem;
  letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer;
  border-radius: 4px; transition: background 0.2s;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #a07f3a; }
`;
const Notice = styled.div<{ $kind: 'error' | 'success' }>`
  margin-top: 1rem; padding: 0.75rem 1rem; border-radius: 4px;
  background: ${({ $kind }) => $kind === 'success' ? 'rgba(101,116,85,0.12)' : 'rgba(198,40,40,0.12)'};
  color: ${({ $kind }) => $kind === 'success' ? '#3b4733' : '#c62828'};
  border-left: 3px solid ${({ $kind }) => $kind === 'success' ? '#657455' : '#c62828'};
`;

const ContactForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [f, setF] = useState({ name: '', email: '', phone: '', message: '', newsletter: false, privacy: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);

  // Prefill message based on ?watch=SHORTCODE query param
  useEffect(() => {
    const shortCode = (searchParams.get('watch') || '').trim();
    if (!shortCode) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/api/watches/by-short-code/${encodeURIComponent(shortCode)}`);
        if (cancelled || res.data?.status !== 'success' || !res.data.watch) return;
        const w = res.data.watch;
        const priceEur = w.priceCents ? Math.floor(parseFloat(String(w.priceCents)) / 100) : null;
        const formatted = priceEur != null
          ? priceEur.toLocaleString(i18n.language === 'en' ? 'en-US' : 'de-DE') + ' €'
          : null;
        const linkBase = typeof window !== 'undefined' ? window.location.origin : '';
        const detailsLink = w.slug && linkBase ? `${linkBase}/kaufen/${w.slug}` : null;

        const lines: string[] = [];
        lines.push(t('contact.prefill_intro'));
        lines.push('');
        if (w.sku) lines.push(`• ${t('contact.prefill_sku')}: ${w.sku}`);
        if (w.brand) lines.push(`• ${t('contact.prefill_brand')}: ${w.brand}`);
        if (w.model) lines.push(`• ${t('contact.prefill_model')}: ${w.model}`);
        if (w.referenceNumber) lines.push(`• ${t('contact.prefill_reference')}: ${w.referenceNumber}`);
        if (formatted) lines.push(`• ${t('contact.prefill_price')}: ${formatted}`);
        if (detailsLink) lines.push(`• ${t('contact.prefill_link')}: ${detailsLink}`);
        lines.push('');
        lines.push(t('contact.prefill_outro'));
        const prefill = lines.join('\n');

        // Only prefill if the user has not started typing
        setF((prev) => (prev.message ? prev : { ...prev, message: prefill }));
      } catch {
        // silent: prefill is best-effort
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, i18n.language]);

  const set = (k: keyof typeof f) => (e: any) =>
    setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(''); setOk(false);
    if (!f.email.trim() || !f.message.trim()) { setErr(t('contact.err_required')); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) { setErr(t('contact.err_email')); return; }
    if (!f.privacy) { setErr(t('contact.err_privacy')); return; }
    setBusy(true);
    try {
      await apiClient.post(`/api/contact`, {
        name: f.name || undefined,
        email: f.email,
        phone: f.phone || undefined,
        message: f.message,
        newsletterOptIn: f.newsletter,
        privacyAccepted: f.privacy,
      });
      setOk(true);
      setF({ name: '', email: '', phone: '', message: '', newsletter: false, privacy: false });
    } catch (e: any) {
      setErr(e.response?.data?.message || t('contact.err_generic'));
    } finally { setBusy(false); }
  };

  return (
    <Wrap onSubmit={submit} noValidate data-testid="contact-form">
      <Row cols={3}>
        <Field>
          <Label>{t('contact.name')}</Label>
          <Input value={f.name} onChange={set('name')} data-testid="ct-name" />
        </Field>
        <Field>
          <Label>{t('contact.email')}*</Label>
          <Input type="email" value={f.email} onChange={set('email')} data-testid="ct-email" />
        </Field>
        <Field>
          <Label>{t('contact.phone')}</Label>
          <Input value={f.phone} onChange={set('phone')} data-testid="ct-phone" />
        </Field>
      </Row>
      <Row cols={1}>
        <Field>
          <Label>{t('contact.message')}*</Label>
          <TextArea value={f.message} onChange={set('message')} maxLength={3000} data-testid="ct-message" />
        </Field>
      </Row>

      <CheckRow>
        <input type="checkbox" checked={f.newsletter} onChange={set('newsletter')} data-testid="ct-newsletter" />
        <span>{t('contact.newsletter_consent')}</span>
      </CheckRow>
      <CheckRow>
        <input type="checkbox" checked={f.privacy} onChange={set('privacy')} data-testid="ct-privacy" />
        <span>
          {t('contact.privacy_consent')}{' '}
          <a href="/datenschutz" target="_blank" rel="noopener noreferrer">{t('contact.privacy_link')}</a>.
        </span>
      </CheckRow>

      <Submit type="submit" disabled={busy} data-testid="ct-submit">
        {busy ? t('contact.submitting') : t('contact.submit')}
      </Submit>

      {err && <Notice $kind="error" data-testid="ct-error">{err}</Notice>}
      {ok && <Notice $kind="success" data-testid="ct-success">{t('contact.success')}</Notice>}
    </Wrap>
  );
};

export default ContactForm;
